/**
 * The Memory control's model (D340, docs/plans/2026-10-memory-globe.md): one set of nodes for every picture, nine layers
 * (our nine ARMS tiers, in the rings' order and colours), where each sits on the globe, how the camera flies to one, how
 * a `/` search ranks them and which links a selection draws. No DOM and no three.js: the page and the tests run it.
 *
 * Ported from RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md):
 *   - brain/port.js  `settle` (the homes: memory as department longitude slices weighted by size with a floor of .035,
 *     golden-angle placement inside a slice, skills on a Fibonacci sphere, apps and routines on rings), `radius` (the
 *     shell radii), `ringVector` (a ring spinning in its own plane while the plane precesses), the ring speeds in
 *     `tick`, `rotate` / `project` (the camera) and the focus move of `QuietPort.focus` (turn the globe so the node
 *     faces the viewer and come closer).
 *   - brain/_core.js `select` / `rebuildFocus` (a selection lights its neighbours and links and dims the rest) and the
 *     name search behind `/`.
 * Our five other tiers (Roadmap, Architecture, Plans, Governance, Library) are outer shells the same way the source's
 * Applications and Routines are: a tilted ring each, further out than Applications, in the order of the rings.
 */
import { ARMS_THEMES, type ArmsGrouping, type ThemeId } from './armsGroupingModel.js';
import { ARMS_TIERS, type ArmsItem, type ArmsItemLink, type ArmsTierId, matchRank } from './armsModel.js';
import { type Folder, folderTree, relativePaths } from './memoryFolders.js';

/** A layer is an ARMS tier. */
export type GlobeLayer = ArmsTierId;

/** The layers, outermost first: the rings' order. */
export const GLOBE_LAYERS: readonly GlobeLayer[] = ARMS_TIERS.map((t) => t.id);

/** The five tiers beyond the source's four: what the repository says about itself. */
export const EXTRA_LAYERS: readonly GlobeLayer[] = ['roadmap', 'architecture', 'plans', 'decisions', 'library'];
/** The four the agent works with: the source's Applications, Routines, Memory and Skills. */
export const CORE_LAYERS: readonly GlobeLayer[] = ['apps', 'routines', 'memory', 'skills'];

/** In the words people use for a layer (the tier `apps` is shown as Applications; `decisions` as Governance). */
export const LAYER_WORDS: Record<GlobeLayer, string> = {
  roadmap: 'Roadmap',
  architecture: 'Architecture',
  plans: 'Plans',
  decisions: 'Governance',
  library: 'Library',
  apps: 'Applications',
  routines: 'Routines',
  memory: 'Memory',
  skills: 'Skills',
};

/** The shells, outside in (port.js `radius` for the four; the five outer ones continue the sequence). */
export const LAYER_RADIUS: Record<GlobeLayer, number> = {
  roadmap: 1.5,
  architecture: 1.4,
  plans: 1.3,
  decisions: 1.2,
  library: 1.1,
  apps: 1,
  routines: 0.81,
  memory: 0.59,
  skills: 0.19,
};

/**
 * The shells under a grouping. Nine tiers: `LAYER_RADIUS`. Five themes: a shell per theme, and the tiers of a theme as bands
 * a little apart inside it (the skills stay the sphere at the middle and memory its sphere).
 */
export function layerRadii(grouping: ArmsGrouping): Record<GlobeLayer, number> {
  if (grouping === 'tiers') return LAYER_RADIUS;
  const base: Record<ThemeId, number> = { direction: 1.5, governance: 1.3, structure: 1.1, kit: 0.92, memory: 0.59 };
  const out = { ...LAYER_RADIUS };
  for (const theme of ARMS_THEMES) theme.tiers.forEach((t, i) => (out[t] = t === 'skills' || t === 'memory' ? LAYER_RADIUS[t] : base[theme.id] - i * 0.07));
  return out;
}

/** Layers drawn as a tilted ring (the source's Applications and Routines, and our five outer shells); the rest are shells. */
export type RingLayer = 'roadmap' | 'architecture' | 'plans' | 'decisions' | 'library' | 'apps' | 'routines';
export const RING_LAYERS: readonly RingLayer[] = ['roadmap', 'architecture', 'plans', 'decisions', 'library', 'apps', 'routines'];
export const isRingLayer = (l: GlobeLayer): l is RingLayer => (RING_LAYERS as readonly string[]).includes(l);

/** A ring's base tilt and its speeds: the source's for Applications and Routines (port.js `ringVector`, `tick`). */
const RING_PARAMS: Record<RingLayer, { tilt: number; ang: number; ax: number }> = {
  apps: { tilt: 0.22, ang: 0.066, ax: 0.042 },
  routines: { tilt: -0.2, ang: -0.0864, ax: -0.0324 },
  library: { tilt: 0.2, ang: -0.05, ax: 0.03 },
  decisions: { tilt: -0.18, ang: 0.045, ax: -0.027 },
  plans: { tilt: 0.16, ang: -0.04, ax: 0.021 },
  architecture: { tilt: -0.2, ang: 0.035, ax: -0.024 },
  roadmap: { tilt: 0.18, ang: -0.03, ax: 0.018 },
};

/** The layer's colour: the same as its ring on the rings picture. */
export function layerColour(layer: GlobeLayer): string {
  return ARMS_TIERS.find((t) => t.id === layer)?.colour ?? '#8a8f98';
}

const TAU = Math.PI * 2;
const GOLDEN = 0.618033988749895;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** What the globe is built from: one entry per thing that gets a node. */
export interface GlobeSource {
  /** `item:<id>` for a ring item, `node:<id>` for a graph node: the keys Find uses. */
  key: string;
  id: string;
  kind: 'item' | 'node' | 'layer' | 'group' | 'dir';
  label: string;
  layer: GlobeLayer;
  /** The department (a community) for memory; the group (connector, scheduled, a kind of skill, a phase) for the rest. */
  dept: string;
  /** A memory node's own topic when its department is "Other topics" (a topic past the cap): the card still names it. */
  topic?: string;
  enabled: boolean;
  detail?: string;
  /** When it last changed (ms), when known. */
  changedAt?: number;
  /** How much it weighs: a memory node's connections, a tool's calls, a skill's runs. At least 1. */
  weight: number;
  /** What kind of thing, for its glyph: a tool's `connector` / `plugin` / `built-in`, a skill's `skill` / `agent` / `hook`. */
  glyph?: string;
}

export const NO_DEPARTMENT = 'Workspace';

export interface GlobeGraphNode {
  id: string;
  label?: string;
  community_name?: string | null;
  source_file?: string;
  /** When the host sends it (git's last commit of the source file), the Timeline places the node on its day. */
  changed_at?: string;
}

export interface GlobeSourceOptions {
  /** `memory` tier items stay out: they are summaries of the same graph nodes. `cap` keeps a huge graph drawable. */
  cap?: number;
  /** An item id to when it last changed (a routine's last run). */
  changed?: ReadonlyMap<string, number>;
  /** An item key (`item:<id>`) to its weight (calls, runs). */
  weights?: ReadonlyMap<string, number>;
  /** The graph's links, for a node's weight (its connections). */
  graphLinks?: ReadonlyArray<{ source: string; target: string }>;
}

/** The group an item is told apart by on its layer: a kind, a schedule, a phase. */
function deptOfItem(i: ArmsItem): string {
  if (i.tier === 'apps' || i.tier === 'routines' || i.tier === 'skills') return i.group ?? NO_DEPARTMENT;
  return i.trail?.[0]?.label ?? i.group ?? NO_DEPARTMENT;
}

/**
 * The sources for the globe: every ring item that is a single thing (tools, routines, skills and the five outer tiers)
 * and every graph node for memory.
 */
export function globeSources(items: readonly ArmsItem[], graph: readonly GlobeGraphNode[], opts: GlobeSourceOptions = {}): { sources: GlobeSource[]; truncated: number } {
  const out: GlobeSource[] = [];
  for (const i of items) {
    if (i.tier === 'memory') continue;
    const key = `item:${i.id}`;
    out.push({
      key,
      id: i.id,
      kind: 'item',
      label: i.label,
      layer: i.tier,
      dept: deptOfItem(i),
      enabled: i.enabled !== false,
      weight: Math.max(1, opts.weights?.get(key) ?? 1 + (i.parts?.length ?? 0)),
      ...(i.group ? { glyph: i.group } : {}),
      ...(i.detail ? { detail: i.detail } : {}),
      ...(opts.changed?.has(i.id) ? { changedAt: opts.changed.get(i.id) } : {}),
    });
  }
  const degree = new Map<string, number>();
  for (const l of opts.graphLinks ?? []) {
    degree.set(l.source, (degree.get(l.source) ?? 0) + 1);
    degree.set(l.target, (degree.get(l.target) ?? 0) + 1);
  }
  const cap = opts.cap ?? 12000;
  const shown = graph.length > cap ? graph.slice(0, cap) : graph;
  for (const n of shown) {
    const t = n.changed_at ? Date.parse(n.changed_at) : NaN;
    out.push({
      key: `node:${n.id}`,
      id: n.id,
      kind: 'node',
      label: n.label ?? n.id,
      layer: 'memory',
      dept: n.community_name || NO_DEPARTMENT,
      enabled: true,
      weight: Math.max(1, degree.get(n.id) ?? 1),
      ...(n.source_file ? { detail: n.source_file } : {}),
      ...(Number.isFinite(t) ? { changedAt: t } : {}),
    });
  }
  return { sources: out, truncated: graph.length - shown.length };
}

/** The view tones (index.css `--tone-*`): a department takes one, and a lighter or darker shade of it past seven. */
export const DEPARTMENT_TONES = ['teal', 'violet', 'amber', 'blue', 'rose', 'green', 'slate'] as const;
export type DepartmentTone = (typeof DEPARTMENT_TONES)[number];
export const TONE_SHADES = 3;

export interface GlobeDepartment {
  key: string;
  label: string;
  index: number;
  count: number;
  tone: DepartmentTone;
  /** 0 the tone as it is, 1 lighter, 2 darker. */
  shade: number;
  /** A stable id for the pair, so two departments never share a colour until twenty-one are drawn. */
  colourId: string;
}

export function departmentColour(index: number): { tone: DepartmentTone; shade: number } {
  return { tone: DEPARTMENT_TONES[index % DEPARTMENT_TONES.length], shade: Math.floor(index / DEPARTMENT_TONES.length) % TONE_SHADES };
}

export interface GlobeNode extends GlobeSource {
  index: number;
  /** The department's index among the memory departments; -1 outside memory. */
  deptIndex: number;
  /** Where it sits on a shell, unit length (memory and skills), or its angle on its ring (the ring layers). */
  home: { x: number; y: number; z: number } | { angle: number };
  ring: boolean;
  /** A structural node (a layer's hub, a group's hub, a folder) rather than a thing of its own; absent for the things themselves. */
  struct?: StructKind;
  /** A theme's hub: which theme. */
  theme?: ThemeId;
  /** The hub or folder it hangs from (an index into `Globe.nodes`); -1 for none. */
  parent: number;
  /** What hangs from a hub or a folder, the heaviest first. */
  children: number[];
  /** A folder: its path from its department's top, and what lies under it. */
  path?: string;
  folder?: FolderInfo;
}

export interface GlobeSector {
  key: string;
  deptIndex: number;
  start: number;
  end: number;
}

export interface Globe {
  nodes: GlobeNode[];
  byKey: Map<string, GlobeNode>;
  departments: GlobeDepartment[];
  sectors: GlobeSector[];
  counts: Record<GlobeLayer, number>;
  /** How far out the outermost layer with anything in it sits: what the camera fits. */
  outer: number;
  /** Spokes (hub to member, folder to file) and routes (layer to group), by node index; empty without structure. */
  edges: GlobeEdge[];
  /** The node of each layer's hub, for the layers that have something in them; empty without structure. */
  layerHubs: Partial<Record<GlobeLayer, number>>;
  structured: boolean;
  /** How the layers are grouped: the shells' radii and the hubs follow it. */
  grouping: ArmsGrouping;
  /** The radius of each layer's shell or ring under this grouping. */
  radii: Record<GlobeLayer, number>;
  /** The node of each theme's hub, under five themes. */
  themeHubs: Partial<Record<ThemeId, number>>;
  /** Memory's topics as the graph has them, and how many are drawn as their own: the rest are folded into "Other topics". */
  topics: { total: number; shown: number; folded: number };
}

/** A tier holding more than this many items shows its groups only, at rest: never the individual items. */
export const REST_BUDGET = 60;
/** Memory's topics drawn as their own (colour, hub, sector): the biggest by size. The rest share one muted topic. */
export const MAX_TOPICS = 12;
export const OTHER_TOPICS = 'Other topics';

const emptyCounts = (): Record<GlobeLayer, number> => Object.fromEntries(GLOBE_LAYERS.map((l) => [l, 0])) as Record<GlobeLayer, number>;

/** A structural node: a theme's hub (five themes), a layer's hub, a group's hub (a department of memory, a phase, a kind), or a folder. */
export type StructKind = 'theme' | 'layer' | 'group' | 'dir';

/** The links that run between nodes, in the source's words: `spoke` hub to member, `route` layer to group, `wire` a link of the graph, `xlink` a link between layers, `sync` (kept for the source's vocabulary; nothing here is synced yet). */
export type LinkKind = 'spoke' | 'route' | 'wire' | 'xlink' | 'sync';
/** How heavy each kind is drawn (the source's weights: spokes thin, routes firm, wires faint). */
export const LINK_WEIGHT: Record<LinkKind, number> = { route: 1.7, spoke: 1, wire: 0.7, xlink: 1.3, sync: 0.9 };

/** What lies under a folder, for its card. */
export interface FolderInfo {
  items: number;
  files: number;
  markdown: number;
}

export interface GlobeEdge {
  a: number;
  b: number;
  kind: 'spoke' | 'route';
}

export interface BuildGlobeOptions {
  /** Nine tiers (the default) or five themes: under themes a hub per theme sits over the hubs of its tiers, and the shells are bands of five. */
  grouping?: ArmsGrouping;
  /** Topics of memory drawn as their own; the rest are folded under "Other topics". Default {@link MAX_TOPICS}. */
  maxTopics?: number;
  /** Add the structure: a hub per layer and per group, a bubble per folder of memory, and the spokes and routes between them. */
  structure?: boolean;
}

/** Lay every source out: its home on its shell. Deterministic: the same sources give the same globe. */
export function buildGlobe(sources: readonly GlobeSource[], opts: BuildGlobeOptions = {}): Globe {
  const structure = !!opts.structure;
  const grouping: ArmsGrouping = opts.grouping ?? 'tiers';
  const byLayer = Object.fromEntries(GLOBE_LAYERS.map((l) => [l, [] as GlobeSource[]])) as Record<GlobeLayer, GlobeSource[]>;
  for (const s of sources) byLayer[s.layer].push(s);
  for (const k of GLOBE_LAYERS) byLayer[k].sort((a, b) => (structure && k !== 'memory' ? a.dept.localeCompare(b.dept, undefined, { numeric: true }) : 0) || a.id.localeCompare(b.id));

  // Topics of memory: the biggest few are drawn as their own, the rest are one muted "Other topics" (a hundred colours and
  // sectors say nothing). The cap is applied here so the globe, the maps, the legend and Find all see the same topics.
  const rawSizes = new Map<string, number>();
  for (const s of byLayer.memory) rawSizes.set(s.dept, (rawSizes.get(s.dept) ?? 0) + 1);
  const topicsTotal = rawSizes.size;
  const maxTopics = Math.max(1, opts.maxTopics ?? MAX_TOPICS);
  if (topicsTotal > maxTopics + 1) {
    const keep = new Set([...rawSizes.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, maxTopics).map(([k]) => k));
    byLayer.memory = byLayer.memory.map((s) => (keep.has(s.dept) ? s : { ...s, dept: OTHER_TOPICS, topic: s.topic ?? s.dept }));
  }
  const sizes = new Map<string, number>();
  for (const s of byLayer.memory) sizes.set(s.dept, (sizes.get(s.dept) ?? 0) + 1);
  // The biggest first so the widest slices are neighbours and the picture is stable as a graph grows; "Other topics" last.
  const ordered = [...sizes.entries()].sort((a, b) => (a[0] === OTHER_TOPICS ? 1 : 0) - (b[0] === OTHER_TOPICS ? 1 : 0) || b[1] - a[1] || a[0].localeCompare(b[0]));
  const departments: GlobeDepartment[] = ordered.map(([key, count], index) => {
    const c = key === OTHER_TOPICS ? { tone: 'slate' as DepartmentTone, shade: 2 } : departmentColour(index);
    return { key, label: key, index, count, ...c, colourId: `${c.tone}:${c.shade}` };
  });

  const nodes: GlobeNode[] = [];
  const parentOf = new Map<GlobeNode, GlobeNode>();
  const routes: Array<[GlobeNode, GlobeNode]> = [];
  const layerHubs = new Map<GlobeLayer, GlobeNode>();
  const groupHubs = new Map<string, GlobeNode>();
  const struct = (kind: StructKind, key: string, label: string, layer: GlobeLayer, dept: string, deptIndex: number, weight: number, home: GlobeNode['home'], extra: Partial<GlobeNode> = {}): GlobeNode => ({
    key,
    id: key,
    kind: kind === 'dir' ? 'dir' : kind === 'group' ? 'group' : 'layer',
    label,
    layer,
    dept,
    enabled: true,
    weight,
    index: 0,
    deptIndex,
    home,
    ring: 'angle' in home,
    struct: kind,
    parent: -1,
    children: [],
    ...extra,
  });
  const layerHubOf = (layer: GlobeLayer): GlobeNode => {
    let hub = layerHubs.get(layer);
    if (!hub) {
      hub = struct('layer', `layerhub:${layer}`, LAYER_WORDS[layer], layer, '', -1, Math.max(1, byLayer[layer].length), isRingLayer(layer) ? { angle: 0 } : { x: 0, y: 1, z: 0 });
      layerHubs.set(layer, hub);
      nodes.push(hub);
    }
    return hub;
  };
  const groupHubOf = (layer: GlobeLayer, dept: string, deptIndex: number, count: number, home: GlobeNode['home']): GlobeNode => {
    const key = `hub:${layer}:${dept}`;
    let hub = groupHubs.get(key);
    if (!hub) {
      hub = struct('group', key, dept, layer, dept, deptIndex, Math.max(1, count), home);
      groupHubs.set(key, hub);
      nodes.push(hub);
      routes.push([layerHubOf(layer), hub]);
      parentOf.set(hub, layerHubOf(layer));
    }
    return hub;
  };

  const sectors: GlobeSector[] = [];
  const total = byLayer.memory.length;
  const weights = departments.map((d) => Math.max(0.035, d.count / Math.max(1, total)));
  const weightSum = weights.reduce((a, b) => a + b, 0) || 1;
  const memPaths = new Map<string, string | undefined>();
  if (structure) {
    const rel = relativePaths(byLayer.memory.map((s) => s.detail));
    byLayer.memory.forEach((s, i) => memPaths.set(s.id, rel[i]));
  }
  let cursor = -Math.PI;
  departments.forEach((d, si) => {
    const width = (TAU * weights[si]) / weightSum;
    const start = cursor + 0.025;
    const span = Math.max(0.01, width - 0.05);
    sectors.push({ key: d.key, deptIndex: d.index, start: cursor, end: cursor + width });
    const mid = cursor + width / 2;
    cursor += width;
    const mine = byLayer.memory.filter((s) => s.dept === d.key);
    const files = new Map<string, GlobeNode>();
    mine.forEach((s, i) => {
      const y = 1 - (2 * (i + 0.5)) / mine.length;
      const a = start + ((i * GOLDEN) % 1) * span;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const node: GlobeNode = { ...s, index: 0, deptIndex: d.index, home: { x: Math.cos(a) * r, y, z: Math.sin(a) * r }, ring: false, parent: -1, children: [] };
      files.set(s.id, node);
      nodes.push(node);
    });
    if (!structure || !mine.length) return;
    const hub = groupHubOf('memory', d.key, d.index, d.count, { x: Math.cos(mid), y: 0, z: Math.sin(mid) });
    const centroid = (ids: readonly string[]) => {
      let x = 0;
      let y = 0;
      let z = 0;
      for (const id of ids) {
        const h = files.get(id)!.home as { x: number; y: number; z: number };
        x += h.x;
        y += h.y;
        z += h.z;
      }
      const len = Math.hypot(x, y, z) || 1;
      return { x: x / len, y: y / len, z: z / len };
    };
    const idsUnder = (f: Folder): string[] => [...f.direct, ...f.folders.flatMap(idsUnder)];
    const tree = folderTree(mine.map((s) => ({ id: s.id, path: memPaths.get(s.id) })));
    const add = (f: Folder, parent: GlobeNode) => {
      const dir = struct('dir', `dir:${d.key}:${f.path}`, f.name, 'memory', d.key, d.index, f.count.items, centroid(idsUnder(f)), { path: f.path, folder: { ...f.count }, detail: f.path });
      nodes.push(dir);
      parentOf.set(dir, parent);
      for (const id of f.direct) parentOf.set(files.get(id)!, dir);
      for (const sub of f.folders) add(sub, dir);
    };
    for (const id of tree.loose) parentOf.set(files.get(id)!, hub);
    for (const f of tree.folders) add(f, hub);
  });
  const sequence = (list: readonly GlobeSource[]) => {
    // With structure each group is a run that opens with its hub: Applications by connector, Roadmap by phase.
    const slots: Array<{ src?: GlobeSource; hub?: string }> = [];
    list.forEach((s, i) => {
      if (structure && (i === 0 || list[i - 1].dept !== s.dept)) slots.push({ hub: s.dept });
      slots.push({ src: s });
    });
    return slots;
  };
  for (const k of RING_LAYERS) {
    const list = byLayer[k];
    const slots = sequence(list);
    slots.forEach((slot, i) => {
      const home = { angle: (i / slots.length) * TAU };
      if (slot.src) nodes.push({ ...slot.src, index: 0, deptIndex: -1, home, ring: true, parent: -1, children: [] });
      else groupHubOf(k, slot.hub!, -1, list.filter((s) => s.dept === slot.hub).length, home);
    });
  }
  {
    const list = byLayer.skills;
    const slots = sequence(list);
    slots.forEach((slot, i) => {
      const y = 1 - (2 * (i + 0.5)) / slots.length;
      const a = i * GOLDEN_ANGLE;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const home = { x: Math.cos(a) * r, y, z: Math.sin(a) * r };
      if (slot.src) nodes.push({ ...slot.src, index: 0, deptIndex: -1, home, ring: false, parent: -1, children: [] });
      else groupHubOf('skills', slot.hub!, -1, list.filter((s) => s.dept === slot.hub).length, home);
    });
  }
  const themeHubs = new Map<ThemeId, GlobeNode>();
  if (structure) {
    for (const k of GLOBE_LAYERS) if (byLayer[k].length) layerHubOf(k);
    if (grouping === 'themes') {
      for (const theme of ARMS_THEMES) {
        const present = theme.tiers.filter((t) => layerHubs.has(t));
        if (!present.length) continue;
        const hub = struct('theme', `themehub:${theme.id}`, theme.title, theme.tiers[0], '', -1, present.reduce((n, t) => n + byLayer[t].length, 0), { x: 0, y: 1, z: 0 }, { theme: theme.id });
        themeHubs.set(theme.id, hub);
        nodes.push(hub);
        for (const t of present) {
          const lh = layerHubs.get(t)!;
          routes.push([hub, lh]);
          parentOf.set(lh, hub);
        }
      }
    }
    // What has no parent yet sits directly under its group's hub.
    for (const n of nodes) {
      if (n.struct || parentOf.has(n)) continue;
      const hub = groupHubs.get(`hub:${n.layer}:${n.dept}`);
      if (hub) parentOf.set(n, hub);
    }
  }
  nodes.forEach((n, i) => (n.index = i));
  const edges: GlobeEdge[] = [];
  for (const [child, parent] of parentOf) {
    child.parent = parent.index;
    parent.children.push(child.index);
    if (parent.struct !== 'layer' && parent.struct !== 'theme') edges.push({ a: parent.index, b: child.index, kind: 'spoke' });
  }
  for (const [a, b] of routes) edges.push({ a: a.index, b: b.index, kind: 'route' });
  for (const n of nodes) if (n.children.length > 1) n.children.sort((x, y) => nodes[y].weight - nodes[x].weight || x - y);
  const counts = emptyCounts();
  for (const k of GLOBE_LAYERS) counts[k] = byLayer[k].length;
  const radii = layerRadii(grouping);
  const outer = Math.max(1, ...GLOBE_LAYERS.filter((k) => counts[k] > 0).map((k) => radii[k]));
  return {
    nodes,
    byKey: new Map(nodes.map((n) => [n.key, n])),
    departments,
    sectors,
    counts,
    outer,
    edges,
    layerHubs: Object.fromEntries([...layerHubs].map(([k, n]) => [k, n.index])) as Partial<Record<GlobeLayer, number>>,
    structured: structure,
    grouping,
    radii,
    themeHubs: Object.fromEntries([...themeHubs].map(([k, n]) => [k, n.index])) as Partial<Record<ThemeId, number>>,
    topics: { total: topicsTotal, shown: departments.length, folded: Math.max(0, topicsTotal - departments.filter((d) => d.key !== OTHER_TOPICS).length) },
  };
}

/* ── Folders: folded and open ────────────────────────────────────────── */

/** "107 topics · 12 shown, the rest under Other topics": the topics line of the legend. */
export function topicsLine(t: { total: number; folded: number }): string {
  const own = t.total - t.folded;
  return t.folded > 0 ? `${t.total.toLocaleString('en-AU')} topics · ${own} shown, the rest under ${OTHER_TOPICS}` : `${t.total.toLocaleString('en-AU')} ${t.total === 1 ? 'topic' : 'topics'}`;
}

/** A hub or folder that opens and closes: every folder of memory, and a group in a tier too big to draw whole at rest. */
export function isFoldable(globe: Pick<Globe, 'counts'>, n: Pick<GlobeNode, 'struct' | 'layer'>): boolean {
  return n.struct === 'dir' || (n.struct === 'group' && globe.counts[n.layer] > REST_BUDGET);
}

/**
 * What is open at first. A tier of up to {@link REST_BUDGET} items is drawn whole (its folders open); a bigger one shows its
 * groups only (a hub per phase, department or kind with its count), each opening on a press.
 */
export function defaultExpanded(globe: Globe, limit = REST_BUDGET): Set<string> {
  return new Set(globe.nodes.filter((n) => n.struct === 'dir' && globe.counts[n.layer] <= limit).map((n) => n.key));
}

/** Which nodes a closed folder or group hides: everything under it, however deep. 1 for hidden. */
export function foldHidden(globe: Globe, expanded: ReadonlySet<string>): Uint8Array {
  const hidden = new Uint8Array(globe.nodes.length);
  const done = new Uint8Array(globe.nodes.length);
  const visit = (i: number): number => {
    if (done[i]) return hidden[i];
    const p = globe.nodes[i].parent;
    let h = 0;
    if (p >= 0) {
      const parent = globe.nodes[p];
      h = (isFoldable(globe, parent) && !expanded.has(parent.key)) || visit(p) ? 1 : 0;
    }
    hidden[i] = h;
    done[i] = 1;
    return h;
  };
  for (let i = 0; i < hidden.length; i++) visit(i);
  return hidden;
}

/** The folders and groups a node sits in, innermost first: what has to be open for it to be seen. */
export function foldersAbove(globe: Globe, index: number): GlobeNode[] {
  const out: GlobeNode[] = [];
  let p = globe.nodes[index]?.parent ?? -1;
  while (p >= 0) {
    const n = globe.nodes[p];
    if (isFoldable(globe, n)) out.push(n);
    p = n.parent;
  }
  return out;
}

/** The set with every folder above these nodes open (a node found in a closed folder is shown, not hidden by it). */
export function expandedFor(globe: Globe, expanded: ReadonlySet<string>, indices: Iterable<number>): ReadonlySet<string> {
  let next: Set<string> | undefined;
  for (const i of indices) {
    for (const f of foldersAbove(globe, i)) {
      if (expanded.has(f.key) || next?.has(f.key)) continue;
      next ??= new Set(expanded);
      next.add(f.key);
    }
  }
  return next ?? expanded;
}

/** The levels open above a node, outermost first, as a breadcrumb: "Roadmap › Phase 3 › …". */
export interface Crumb {
  key: string;
  label: string;
  /** A layer or theme hub: where the trail starts, not something that closes. */
  root: boolean;
  open: boolean;
}

export function breadcrumbOf(globe: Globe, expanded: ReadonlySet<string>, key: string | null): Crumb[] {
  const node = key ? globe.byKey.get(key) : undefined;
  if (!node) return [];
  const chain: GlobeNode[] = [];
  let p = node.index;
  while (p >= 0) {
    chain.unshift(globe.nodes[p]);
    p = globe.nodes[p].parent;
  }
  const crumbs = chain.filter((n) => n.struct || n === node).map<Crumb>((n) => ({ key: n.key, label: n.struct === 'dir' ? `${n.label}/` : n.label, root: n.struct === 'layer' || n.struct === 'theme', open: isFoldable(globe, n) && expanded.has(n.key) }));
  // Only worth showing while something above or at the selection is open.
  return crumbs.some((c) => c.open) ? crumbs : [];
}

/**
 * Back (and Escape): close the deepest open level the selection sits in, and select it so the picture stays where the person is.
 * With nothing open above the selection, the selection itself lets go.
 */
export function backStep(globe: Globe, expanded: ReadonlySet<string>, key: string | null): { close?: string; select?: string | null } {
  const node = key ? globe.byKey.get(key) : undefined;
  if (!node) return {};
  const open = [node, ...foldersAbove(globe, node.index)].filter((n) => isFoldable(globe, n) && expanded.has(n.key));
  const deepest = open[0];
  if (!deepest) return { select: null };
  return { close: deepest.key, select: deepest.key };
}

/* ── Rings ───────────────────────────────────────────────────────────── */

/** Each ring's angle and the direction its plane has turned to (port.js `ringAngles` / `ringAxis`). */
export type RingState = Record<RingLayer, { ang: number; ax: number }>;
export const RINGS_AT_REST: RingState = Object.fromEntries(RING_LAYERS.map((k) => [k, { ang: 0, ax: 0 }])) as RingState;

/** Advance the rings by `dt` seconds (port.js `tick`'s speeds; the outer rings turn slower). */
export function stepRings(s: RingState, dt: number): RingState {
  return Object.fromEntries(RING_LAYERS.map((k) => [k, { ang: s[k].ang + dt * RING_PARAMS[k].ang, ax: s[k].ax + dt * RING_PARAMS[k].ax }])) as RingState;
}

/** A point of ring `k` at `angle` as a unit vector (port.js `ringVector`): the plane tilts and its direction turns. */
export function ringVector(k: RingLayer, angle: number, s: RingState): { x: number; y: number; z: number } {
  const phi = s[k].ax;
  const tilt = RING_PARAMS[k].tilt * (1 + 0.4 * Math.sin(phi * 1.7));
  const a = angle + s[k].ang;
  const x0 = Math.cos(a);
  const z0 = Math.sin(a);
  const y = Math.sin(tilt) * z0;
  const z1 = Math.cos(tilt) * z0;
  const c = Math.cos(phi);
  const sn = Math.sin(phi);
  return { x: x0 * c + z1 * sn, y, z: -x0 * sn + z1 * c };
}

/** Where a node is, in globe units (the applications' shell has radius 1). */
export function positionOf(n: GlobeNode, rings: RingState, radii: Record<GlobeLayer, number> = LAYER_RADIUS): { x: number; y: number; z: number } {
  const r = radii[n.layer];
  const v = 'angle' in n.home ? ringVector(n.layer as RingLayer, n.home.angle, rings) : n.home;
  return { x: v.x * r, y: v.y * r, z: v.z * r };
}

/* ── Camera ──────────────────────────────────────────────────────────── */

/** Turned by `yaw` about the vertical, then tipped by `pitch`; `zoom` 1 fits the whole globe; the view may be panned. */
export interface GlobeCamera {
  yaw: number;
  pitch: number;
  zoom: number;
  panX: number;
  panY: number;
}
export const CAMERA_HOME: GlobeCamera = { yaw: 0.27, pitch: 0.3, zoom: 1, panX: 0, panY: 0 };
export const CAMERA_MIN_ZOOM = 0.45;
export const CAMERA_MAX_ZOOM = 9;
export const PITCH_LIMIT = 1.45;

export function clampCamera(c: GlobeCamera): GlobeCamera {
  return { yaw: c.yaw, pitch: Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, c.pitch)), zoom: Math.max(CAMERA_MIN_ZOOM, Math.min(CAMERA_MAX_ZOOM, c.zoom)), panX: c.panX, panY: c.panY };
}

/** A point turned by the camera (port.js `rotate`): x right, y up, z towards the viewer. */
export function rotatePoint(p: { x: number; y: number; z: number }, yaw: number, pitch: number): { x: number; y: number; z: number } {
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cx = Math.cos(pitch);
  const sx = Math.sin(pitch);
  const x = p.x * cy + p.z * sy;
  const z = -p.x * sy + p.z * cy;
  return { x, y: p.y * cx - z * sx, z: p.y * sx + z * cx };
}

/** The camera that brings a point to the middle of the view, facing the viewer, and comes closer: the fly-to (`QuietPort.focus`). */
export function flyTo(p: { x: number; y: number; z: number }, zoom?: number): GlobeCamera {
  const rho = Math.hypot(p.x, p.z);
  const len = Math.hypot(p.x, p.y, p.z);
  if (len < 1e-6) return { ...CAMERA_HOME, zoom: zoom ?? 1.4 };
  return clampCamera({ yaw: Math.atan2(-p.x, p.z), pitch: Math.atan2(p.y, rho), zoom: zoom ?? (len > 0.5 ? 2.1 : 1.6), panX: 0, panY: 0 });
}

const wrap = (a: number) => a - TAU * Math.round(a / TAU);

/** Between two cameras, `e` from 0 to 1 already eased; yaw takes the short way round. */
export function blendCamera(from: GlobeCamera, to: GlobeCamera, e: number): GlobeCamera {
  const t = Math.max(0, Math.min(1, e));
  return {
    yaw: from.yaw + wrap(to.yaw - from.yaw) * t,
    pitch: from.pitch + (to.pitch - from.pitch) * t,
    zoom: from.zoom * Math.pow(to.zoom / from.zoom, t),
    panX: from.panX + (to.panX - from.panX) * t,
    panY: from.panY + (to.panY - from.panY) * t,
  };
}

/* ── Search ──────────────────────────────────────────────────────────── */

export interface GlobeHit {
  node: GlobeNode;
  rank: number;
}

/** Every node that answers a query, best first: the name before the department, the start of a name before its middle. */
export function searchGlobe(nodes: readonly GlobeNode[], query: string, limit = 40): GlobeHit[] {
  const q = query.trim();
  if (!q) return [];
  const hits: GlobeHit[] = [];
  for (const node of nodes) {
    if (node.struct) continue;
    const rank = matchRank(q, node.label, node.dept === NO_DEPARTMENT ? undefined : node.dept, node.detail);
    if (rank >= 0) hits.push({ node, rank });
  }
  hits.sort((a, b) => a.rank - b.rank || a.node.label.localeCompare(b.node.label, undefined, { numeric: true }));
  return hits.slice(0, limit);
}

/** Enter on a search: where the camera goes for the best answer, and what it is; nothing when nothing answers. */
export function searchFlight(nodes: readonly GlobeNode[], query: string, rings: RingState, radii: Record<GlobeLayer, number> = LAYER_RADIUS): { node: GlobeNode; camera: GlobeCamera } | undefined {
  const best = searchGlobe(nodes, query, 1)[0];
  return best ? { node: best.node, camera: flyTo(positionOf(best.node, rings, radii)) } : undefined;
}

/* ── Connections ─────────────────────────────────────────────────────── */

/** Who is linked to whom, by node index: the graph's links among memory nodes, the rings' links among items, and the spokes and routes of the structure. */
export function buildAdjacency(globe: Globe, graphLinks: ReadonlyArray<{ source: string; target: string }>, itemLinks: readonly Pick<ArmsItemLink, 'source' | 'target'>[] = []): Array<number[]> {
  const adj: Array<number[]> = globe.nodes.map(() => []);
  const add = (a: GlobeNode | undefined, b: GlobeNode | undefined) => {
    if (!a || !b || a === b) return;
    adj[a.index].push(b.index);
    adj[b.index].push(a.index);
  };
  for (const l of graphLinks) add(globe.byKey.get(`node:${l.source}`), globe.byKey.get(`node:${l.target}`));
  for (const l of itemLinks) add(globe.byKey.get(`item:${l.source}`), globe.byKey.get(`item:${l.target}`));
  for (const e of globe.edges) add(globe.nodes[e.a], globe.nodes[e.b]);
  return adj;
}

/** One neighbour of a node, as the card lists it: who, by which kind of link, in which words, and how many links say so. */
export interface Neighbour {
  index: number;
  kind: LinkKind;
  /** The relation in words ("calls", "uses", "holds"); empty when the link says none. */
  relation: string;
  /** How many links join the pair: shown as ×N when more than one. */
  count: number;
}

/** The card's list is this long at most. */
export const MAX_NEIGHBOURS = 16;

/** Every node's neighbours with the kind of link and its words, by node index, the strongest first. */
export function buildNeighbours(
  globe: Globe,
  graphLinks: ReadonlyArray<{ source: string; target: string; relation?: string }>,
  itemLinks: readonly Pick<ArmsItemLink, 'source' | 'target' | 'relation'>[] = [],
): Array<Neighbour[]> {
  const by: Array<Map<number, Neighbour>> = globe.nodes.map(() => new Map());
  const put = (from: number, to: number, kind: LinkKind, relation: string) => {
    const m = by[from];
    const at = m.get(to);
    if (at) {
      at.count += 1;
      if (!at.relation && relation) at.relation = relation;
    } else m.set(to, { index: to, kind, relation, count: 1 });
  };
  const both = (a: GlobeNode | undefined, b: GlobeNode | undefined, kind: LinkKind, relation: string, back = relation) => {
    if (!a || !b || a === b) return;
    put(a.index, b.index, kind, relation);
    put(b.index, a.index, kind, back);
  };
  for (const l of graphLinks) both(globe.byKey.get(`node:${l.source}`), globe.byKey.get(`node:${l.target}`), 'wire', l.relation ?? 'linked');
  for (const l of itemLinks) both(globe.byKey.get(`item:${l.source}`), globe.byKey.get(`item:${l.target}`), 'xlink', l.relation);
  for (const e of globe.edges) {
    const a = globe.nodes[e.a];
    const b = globe.nodes[e.b];
    if (e.kind === 'route') both(a, b, 'route', 'holds', 'in layer');
    else both(a, b, 'spoke', 'holds', a.struct === 'dir' ? 'in folder' : 'in group');
  }
  return by.map((m) => [...m.values()].sort((x, y) => y.count - x.count || globe.nodes[y.index].weight - globe.nodes[x.index].weight || x.index - y.index));
}

export const MAX_CONNECTIONS = 24;

export interface Connection {
  index: number;
  /** 1 a direct neighbour, 2 a neighbour's neighbour: it fades more. */
  hops: 1 | 2;
}

/**
 * What a selection draws: its neighbours first (the heaviest first), then their neighbours while there is room, at most
 * `max`. A node with no links draws none.
 */
export function connectionsOf(globe: Globe, adj: ReadonlyArray<readonly number[]>, index: number, max = MAX_CONNECTIONS): Connection[] {
  const weight = (i: number) => globe.nodes[i].weight;
  const first = [...new Set(adj[index] ?? [])].filter((i) => i !== index).sort((a, b) => weight(b) - weight(a) || a - b);
  const out: Connection[] = first.slice(0, max).map((i) => ({ index: i, hops: 1 as const }));
  if (out.length >= max) return out;
  const seen = new Set<number>([index, ...first]);
  const second: number[] = [];
  for (const f of first) {
    for (const j of adj[f] ?? []) {
      if (seen.has(j)) continue;
      seen.add(j);
      second.push(j);
    }
  }
  second.sort((a, b) => weight(b) - weight(a) || a - b);
  for (const j of second) {
    if (out.length >= max) break;
    out.push({ index: j, hops: 2 });
  }
  return out;
}

/** How strongly a connection is drawn: a direct neighbour fully, a second hop at less, both fading with the distance on screen. */
export function connectionAlpha(hops: 1 | 2, distance: number, reach: number): number {
  const fade = 1 - 0.7 * Math.min(1, distance / Math.max(1, reach));
  return (hops === 1 ? 0.85 : 0.4) * fade;
}
